import React from 'react';
import { AlertTriangle, CheckCircle2, ClipboardList, Clock3, History, RefreshCw, UserRoundPen } from 'lucide-react';
import { formatDate, getSemaforoFromDays, SEMAFORO_META } from './gestorVentasUtils';

const ESTADO_BADGE = {
  'PRE-APROBADO': 'bg-amber-100 text-amber-800 ring-amber-200',
  APROBADO: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};

function SemaforoFecha({ row }) {
  const code = row.semaforo || getSemaforoFromDays(row.diasSinGestion);
  const meta = SEMAFORO_META[code] || SEMAFORO_META.ROJO;
  return (
    <div className="min-w-[145px]">
      <div className="font-medium text-slate-700">{formatDate(row.fechaBaseSemaforo)}</div>
      <span className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${meta.className}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${meta.dotClassName}`} aria-hidden="true" />
        {row.diasSinGestion} {Number(row.diasSinGestion) === 1 ? 'día' : 'días'} · {meta.label}
      </span>
    </div>
  );
}

function Acciones({ row, onGestionar, onHistorial, onReasignar, puedeReasignar }) {
  return (
    <div className="flex items-center gap-1">
      {row.estadoCola !== 'CERRADA' && (
        <button
          type="button"
          onClick={() => onGestionar(row)}
          className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg bg-morado px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-moradoHover focus:outline-none focus:ring-2 focus:ring-morado/40"
          aria-label={`Gestionar a ${row.nombreCompleto}`}
        >
          <ClipboardList size={14} /> Gestionar
        </button>
      )}
      {Number(row.cantidadGestiones) > 0 && (
        <button
          type="button"
          onClick={() => onHistorial(row)}
          className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-morado/30"
          aria-label={`Ver historial de ${row.nombreCompleto}`}
          title="Ver historial"
        >
          <History size={15} />
        </button>
      )}
      {puedeReasignar && Number(row.idNivel) === 4 && row.estadoCola !== 'CERRADA' && (
        <button type="button" onClick={() => onReasignar(row)}
          className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-morado/30"
          aria-label={`Reasignar a ${row.nombreCompleto}`} title="Reasignar Reiterativo">
          <UserRoundPen size={16} />
        </button>
      )}
    </div>
  );
}

function Gestionado({ row }) {
  return row.gestionado ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 font-semibold text-emerald-700 ring-1 ring-emerald-200">
      <CheckCircle2 size={12} /> Sí ({row.cantidadGestiones})
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-600 ring-1 ring-slate-200">
      <Clock3 size={12} /> No
    </span>
  );
}

function NivelJerarquia({ row, align = 'left' }) {
  return (
    <div className={`flex flex-col gap-1 ${align === 'right' ? 'items-end' : 'items-start'}`}>
      <span className="rounded-full bg-violet-100 px-2 py-1 font-bold text-violet-800 ring-1 ring-violet-200">
        Nivel {row.prioridad} · {row.nivel}
      </span>
      {row.subnivel && (
        <span className="max-w-[230px] text-[10px] font-semibold leading-4 text-slate-600">
          Subnivel {row.prioridadSubnivel} · {row.subnivel}
        </span>
      )}
    </div>
  );
}

function EstadoOportunidad({ row }) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <span className={`rounded-full px-2 py-1 font-bold ring-1 ${ESTADO_BADGE[row.estado] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
        {row.estado || '—'}
      </span>
      {row.resentido && (
        <span className="rounded-full bg-rose-100 px-2 py-1 text-[10px] font-bold text-rose-800 ring-1 ring-rose-200">
          Resentido
        </span>
      )}
    </div>
  );
}

function MobileCard({ row, onGestionar, onHistorial, onReasignar, puedeReasignar }) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:hidden">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">{row.nombreCompleto}</h3>
          <p className="mt-0.5 text-sm text-slate-600">{row.cedula} · {row.telefono || 'Sin teléfono'}</p>
          {row.origen && <p className="mt-1 text-xs font-semibold text-violet-700">Origen: {row.origen}</p>}
        </div>
        <NivelJerarquia row={row} align="right" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div><span className="block text-xs text-slate-500">Producto</span>{row.productoOriginal || '—'}</div>
        <div><span className="mb-1 block text-xs text-slate-500">Estado</span><EstadoOportunidad row={row} /></div>
        <div className="col-span-2"><SemaforoFecha row={row} /></div>
        <div><span className="block text-xs text-slate-500">Gestionado</span><Gestionado row={row} /></div>
        <div><span className="block text-xs text-slate-500">Próxima gestión</span>{formatDate(row.fechaProximaGestion)}</div>
      </div>
      {row.escalamientoPendiente && (
        <p className="mt-3 flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          <AlertTriangle size={14} /> Escalamiento al jefe pendiente
        </p>
      )}
      <div className="mt-4"><Acciones row={row} onGestionar={onGestionar} onHistorial={onHistorial} onReasignar={onReasignar} puedeReasignar={puedeReasignar} /></div>
    </article>
  );
}

export function ClientesAprobadosDetalle({ estado, onGestionar, onHistorial, onReasignar }) {
  const { loading, error, data = [] } = estado || {};

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center gap-2 text-slate-600" role="status">
        <RefreshCw size={18} className="animate-spin text-morado motion-reduce:animate-none" />
        <span className="text-sm font-medium">Cargando oportunidades…</span>
      </div>
    );
  }
  if (error) return <div className="flex min-h-full items-center justify-center"><div className="w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</div></div>;
  if (!data.length) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 text-center text-slate-600">
        <ClipboardList size={24} className="text-slate-400" />
        <p className="text-sm font-medium">No hay oportunidades en esta vista.</p>
        <p className="text-xs">Prueba otra pestaña, nivel o término de búsqueda.</p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0">
      <div className="h-full space-y-3 overflow-y-auto overscroll-contain pr-1 md:hidden">
        {data.map(row => <MobileCard key={row.idOportunidad} row={row} onGestionar={onGestionar} onHistorial={onHistorial} onReasignar={onReasignar} puedeReasignar={estado.puedeReasignar} />)}
      </div>
      <div className="hidden h-full min-h-0 overflow-auto overscroll-contain rounded-xl border border-slate-200 md:block">
        <table className="min-w-[1420px] w-full text-xs">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-600 shadow-sm">
            <tr>
              <th className="px-3 py-3">Acción</th><th className="px-3 py-3">Cédula</th><th className="px-3 py-3">Nombre</th>
              <th className="px-3 py-3">Teléfono</th><th className="px-3 py-3">Nivel / Subnivel</th><th className="px-3 py-3">Producto</th>
              <th className="px-3 py-3">Fecha / Semáforo</th><th className="px-3 py-3">Estado</th>
              <th className="px-3 py-3">Verif. documental</th><th className="px-3 py-3">Verif. telefónica</th><th className="px-3 py-3">Gestionado</th>
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <tr key={row.idOportunidad} className="border-t border-slate-100 align-top transition-colors hover:bg-violet-50/40">
                <td className="px-3 py-3"><Acciones row={row} onGestionar={onGestionar} onHistorial={onHistorial} onReasignar={onReasignar} puedeReasignar={estado.puedeReasignar} /></td>
                <td className="px-3 py-3 font-semibold text-slate-800">{row.cedula}</td>
                <td className="max-w-[220px] px-3 py-3 text-slate-700">{row.nombreCompleto}{row.origen && <span className="mt-1 block text-[10px] font-semibold text-violet-700">{row.origen}</span>}</td>
                <td className="px-3 py-3 text-slate-700">{row.telefono || '—'}</td>
                <td className="px-3 py-3"><NivelJerarquia row={row} /></td>
                <td className="max-w-[170px] px-3 py-3 text-slate-700">{row.productoOriginal || '—'}</td>
                <td className="px-3 py-3"><SemaforoFecha row={row} /></td>
                <td className="px-3 py-3"><EstadoOportunidad row={row} /></td>
                <td className="max-w-[160px] px-3 py-3 text-slate-700">{row.verificacionDocumental || '—'}</td>
                <td className="max-w-[160px] px-3 py-3 text-slate-700">{row.verificacionTelefonica || '—'}</td>
                <td className="px-3 py-3"><Gestionado row={row} />{row.escalamientoPendiente && <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-red-700"><AlertTriangle size={11} /> Escalar</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
